import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Check, Copy, X, Share2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { ShareHeaderColors } from './ShareHeaderColors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ShareModal({
  shareUrl,
  copied,
  copyToClipboard,
  handleRevokeShare,
  onClose,
  eventName,
  eventId,
  shareTheme,
  onThemeSaved,
}) {
  const [sharing, setSharing] = useState(false);
  const displayName = (eventName || '').trim() || 'Tournament Schedule';
  const shareText = `${displayName} — check out the full schedule.`;

  // Extract share_token from the URL so we can fetch the matching promo PNG
  // (the system-generated tournament card with event name + organization logo).
  const shareTokenMatch = (shareUrl || '').match(/\/share\/([^/?#]+)/);
  const shareToken = shareTokenMatch ? shareTokenMatch[1] : null;

  // Native share — pulls the federation-branded promo PNG (event name + org logo)
  // from the public endpoint and passes it to the OS share sheet. This makes
  // every share via WhatsApp, Messages, AirDrop, etc. carry the proper branded
  // preview instead of just a raw URL.
  const handleNativeShare = async () => {
    if (!navigator.share) {
      // Fallback for desktop browsers without Web Share API: copy just the
      // URL so pasting into any chat app produces a clean, clickable link.
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      } catch {
        toast.error('Could not copy — please copy the link manually.');
      }
      return;
    }

    setSharing(true);
    let sharePayload = { title: displayName, text: shareText, url: shareUrl };

    // Try to include the promo image so the share sheet renders the
    // co-branded card directly (rather than relying on the recipient's app
    // to fetch OG tags).
    if (shareToken) {
      try {
        const res = await fetch(`${API_URL}/api/share/${shareToken}/promo-image?size=og`);
        if (res.ok) {
          const blob = await res.blob();
          const file = new File([blob], `${displayName.replace(/[^a-z0-9]+/gi, '-')}.png`, { type: 'image/png' });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            sharePayload = { files: [file], title: displayName, text: shareText, url: shareUrl };
          }
        }
      } catch {
        // ignore — fall back to URL-only share
      }
    }

    try {
      await navigator.share(sharePayload);
    } catch (e) {
      // User cancellation throws AbortError — silent.
      if (e && e.name !== 'AbortError') {
        toast.error('Share cancelled or failed.');
      }
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4 border-slate-200 shadow-xl max-h-[92vh] overflow-y-auto" data-testid="share-modal">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>
            Share Schedule
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Anyone with this link can view <span className="font-semibold text-slate-800">{displayName}</span> (read-only).
          </p>

          {/* Primary native-share CTA — tournament name + branded promo card auto-included */}
          <Button
            type="button"
            className="w-full bg-[#00247D] hover:bg-[#001A5C] text-white"
            onClick={handleNativeShare}
            disabled={sharing}
            data-testid="share-native-btn"
          >
            {sharing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing share card…</>
            ) : (
              <><Share2 className="w-4 h-4 mr-2" /> Share with name & logo</>
            )}
          </Button>

          <div className="flex items-center gap-2 text-[11px] text-slate-400 uppercase tracking-wider">
            <div className="flex-1 h-px bg-slate-200" />
            <span>or copy the link</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <div className="flex gap-2">
            <Input
              value={shareUrl}
              readOnly
              className="font-mono text-sm bg-slate-50"
              data-testid="share-url-input"
            />
            <Button
              variant="outline"
              onClick={copyToClipboard}
              data-testid="copy-share-link-btn"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>

          {/* Social Share Buttons */}
          <div className="pt-2">
            <p className="text-sm text-slate-500 mb-3">Share on social media:</p>
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                size="icon"
                className="w-12 h-12 rounded-full border-[#1DA1F2] text-[#1DA1F2] hover:bg-[#1DA1F2] hover:text-white transition-colors"
                onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out this tournament schedule: ${displayName}`)}&url=${encodeURIComponent(shareUrl)}`, '_blank')}
                data-testid="share-twitter-btn"
                title="Share on Twitter/X"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="w-12 h-12 rounded-full border-[#1877F2] text-[#1877F2] hover:bg-[#1877F2] hover:text-white transition-colors"
                onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank')}
                data-testid="share-facebook-btn"
                title="Share on Facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd"/>
                </svg>
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="w-12 h-12 rounded-full border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white transition-colors"
                onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Check out this tournament schedule: ${displayName} - ${shareUrl}`)}`, '_blank')}
                data-testid="share-whatsapp-btn"
                title="Share on WhatsApp"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </Button>
            </div>
          </div>

          {eventId && (
            <ShareHeaderColors eventId={eventId} eventName={eventName} initialTheme={shareTheme} onSaved={onThemeSaved} />
          )}

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
              onClick={handleRevokeShare}
              data-testid="revoke-share-btn"
            >
              Revoke Link
            </Button>
            <Button
              className="flex-1 bg-[#00247D] hover:bg-[#001A5C]"
              onClick={onClose}
            >
              Done
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
